import { loadFacilityOptions } from '@api/loadOptions'
import { useUnit } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAppSelector } from '@store/hooks'
import { UnitDataMock, UnitIncludedMock } from '@type/unit'
import { FormButton, FormInputField, Modal, TextAreaField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import useFormPersist from 'react-hook-form-persist'
import { InferType, object, string } from 'yup'

type Props = {
  defaultData?: UnitDataMock
  onClose: () => void
  includedData?: UnitIncludedMock[]
}

const EMPTY_VALUES: UnitInputProps = {
  code: '',
  department: '',
  description: '',
  facility: '',
  name: ''
}

export const unitSchema = object({
  code: string().required('Code is required'),
  department: string().required('Department is required'),
  description: string(),
  facility: string().required('Facility is required'),
  name: string().required('Name is required')
})

export type UnitInputProps = InferType<typeof unitSchema>

function unitToFormValues(unit: UnitDataMock): UnitInputProps {
  return {
    code: unit.attributes.code ?? '',
    department: String(unit.relationships.department.data?.id ?? ''),
    description: (unit.attributes.description as string) ?? '',
    facility: String(unit.relationships?.facility?.data?.id ?? ''),
    name: unit.attributes.name ?? ''
  }
}

export const AddUnit = ({ defaultData, onClose, includedData }: Props) => {
  const isEdit = Boolean(defaultData?.id)
  const { departments } = useAppSelector(state => state.config)
  const editValues = useMemo(() => (defaultData ? unitToFormValues(defaultData) : null), [defaultData])
  const persistKey = isEdit ? `editUnit:${defaultData!.id}` : 'addUnit'

  const defaultFacilityId = defaultData?.relationships?.facility?.data?.id

  const facilityNameFromIncluded = useMemo(() => {
    if (!defaultFacilityId || !includedData?.length) return undefined
    return includedData.find(i => i.type === 'facility' && i.id === defaultFacilityId)?.attributes?.name
  }, [defaultFacilityId, includedData])

  const departmentOptions = useMemo(
    () => departments?.map(department => ({ label: department.name, value: department.id })) || [],
    [departments]
  )

  const methods = useForm<UnitInputProps>({
    defaultValues: EMPTY_VALUES,
    resolver: yupResolver(unitSchema),
    ...(editValues ? { values: editValues } : {})
  })
  const {
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting }
  } = methods

  const { createUnit, updateUnit } = useUnit({})

  const { clear } = useFormPersist(persistKey, { setValue, storage: window.localStorage, watch })

  const [facilitySeed, setFacilitySeed] = useState<{ label: string; value: string } | null>(null)

  useEffect(() => {
    if (!defaultData?.relationships?.facility?.data?.id) {
      setFacilitySeed(null)
      return
    }
    const facilityId = String(defaultData.relationships.facility.data.id)
    if (facilityNameFromIncluded) {
      setFacilitySeed({ label: facilityNameFromIncluded, value: facilityId })
    }
    let cancelled = false
    loadFacilityOptions('')
      .then((options: { label: string; value: string }[]) => {
        if (cancelled) return
        const found = options.find(o => String(o.value) === facilityId)
        setFacilitySeed(found ?? { label: facilityNameFromIncluded ?? 'Selected facility', value: facilityId })
      })
      .catch(() => {
        if (!cancelled) {
          setFacilitySeed({
            label: facilityNameFromIncluded ?? 'Selected facility',
            value: facilityId
          })
        }
      })
    return () => {
      cancelled = true
    }
  }, [defaultData?.id, defaultData?.relationships?.facility?.data?.id, facilityNameFromIncluded])

  const handleClose = () => {
    onClose()
    reset(EMPTY_VALUES)
    clear()
    setFacilitySeed(null)
  }

  const onSubmit = async ({ department, facility, ...rest }: UnitInputProps) => {
    const relationships = {
      department: {
        data: {
          id: department,
          type: 'department'
        }
      },
      facility: {
        data: {
          id: facility,
          type: 'facility'
        }
      }
    }

    if (isEdit && defaultData) {
      const payload = {
        data: {
          attributes: {
            ...rest
          },
          id: defaultData.id,
          relationships,
          type: 'units'
        }
      }
      try {
        await updateUnit({ data: payload, id: defaultData.id })
        showToast('Unit updated successfully')
        handleClose()
      } catch (error) {
        getError(error)
      }
      return
    }

    const payload = {
      data: {
        attributes: {
          ...rest
        },
        relationships,
        type: 'units'
      }
    }

    try {
      await createUnit(payload)
      showToast('Unit added successfully')
      handleClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={handleClose} reset={reset} title={isEdit ? 'Edit Unit' : 'Add Unit'}>
        <form className="flex max-h-[88vh] min-w-[450px] flex-col overflow-auto" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-y-4 overflow-auto p-6">
            <div className="mx-auto flex w-full max-w-[399px] flex-col gap-4">
              <div>
                <FormInputField label="Name" name="name" placeholder="" />
              </div>
              <Controller
                control={methods.control}
                name="facility"
                render={({ field: { onChange, value }, fieldState: { error } }) => {
                  let selected: { label: string; value: string } | null = null
                  if (value) {
                    if (facilitySeed && String(facilitySeed.value) === String(value)) {
                      selected = facilitySeed
                    } else if (
                      defaultFacilityId &&
                      String(value) === String(defaultFacilityId) &&
                      facilityNameFromIncluded
                    ) {
                      selected = { label: facilityNameFromIncluded, value: String(value) }
                    } else {
                      selected = { label: String(value), value: String(value) }
                    }
                  }

                  return (
                    <Select
                      borderRadius={'100px'}
                      cacheOptions
                      error={error?.message}
                      getOptionLabel={({ label }: any) => label}
                      getOptionValue={({ value: v }: any) => v}
                      isAsync
                      label="Facility"
                      loadOptions={loadFacilityOptions}
                      name="facility"
                      onChange={(newValue: any) => {
                        const next = newValue?.value || ''
                        onChange(next)
                        if (newValue?.label && newValue?.value) {
                          setFacilitySeed({ label: newValue.label, value: String(newValue.value) })
                        }
                      }}
                      placeholder="E.g Hospital"
                      value={selected}
                    />
                  )
                }}
              />
              <div>
                <FormInputField label="Code" name="code" placeholder="" />
              </div>
              <div>
                <Controller
                  control={methods.control}
                  name="department"
                  render={({ field: { onChange, value }, fieldState: { error } }) => {
                    return (
                      <Select
                        borderRadius={'100px'}
                        getOptionLabel={({ label }: any) => label}
                        getOptionValue={({ value }: any) => value}
                        label="Department"
                        name="department"
                        onChange={(newValue: any) => {
                          onChange(newValue?.value)
                        }}
                        options={departmentOptions}
                        placeholder="E.g surgery"
                        value={departmentOptions?.find(department => department.value.toString() === value)}
                      />
                    )
                  }}
                />
              </div>
              <div>
                <TextAreaField label="Description" name="description" />
              </div>
            </div>
          </div>
          <div>
            <FormButton isLoading={isSubmitting} onClose={handleClose} submit={isEdit ? 'Update' : undefined} />
          </div>
        </form>
      </Modal>
    </FormProvider>
  )
}

/** Same form as add; opened via `EDIT_UNIT_MODAL` with `defaultData`. */
export const EditUnit = AddUnit
